import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import Select from 'react-select';
import InfiniteScroll from 'react-infinite-scroller';

import { IMultimedia } from '../../Models/Multimedia/IMultimedia';
import { MultimediaService } from '../../Services';
import { WidgetHelper } from '../WidgetHelper';
import MultimediaPreview from './MultimediaPreview';
import MultimediaDetail from './MultimediaDetail';
import { IDropDownValue } from '../../Models';

interface IMultimediaListWidgetState {
    multimedias: IMultimedia[] | null;
    multimediasTotalCount: number;
    selectedMultimedia?: IMultimedia | null;
    searchText: string;
    displayMore: boolean;
    isDisplayMoreBeenClicked: boolean;
    hasMore: boolean;
    themes: IDropDownValue[];
    selectedThemes: string[];
    isSearchTargetDisplayed: boolean;
    selectedMedias?: IMultimedia[] | null;
}

interface IMultimediaListWidgetProps {
    dictEmptyList: string;
    dictReadMore: string;
    dictSearchPlaceholder: string;
    dictBackLabel: string;
    dictSelectDomain: string;
    dictSelectedThemes: string;
    dictTheMostRead: string;
    dictSimilarMedias: string;
    area: string;
    selectedMultimedia?: IMultimedia | string;
    lang: string;
    selectedMedias?: IMultimedia[] | string;
}

interface IMultimediaBackendTag {
    Id?: string | number;
    id?: string | number;
    Title?: string;
    title?: string;
}

interface IMultimediaBackend {
    Id?: string | number;
    id?: string | number;
    Title?: string;
    title?: string;
    CreationDate?: string;
    creationDate?: string;
    ThumbnailUrl?: string;
    thumbnailUrl?: string;
    YoutubeLink?: string;
    youtubeLink?: string;
    ReadingTime?: number;
    readingTime?: number;
    InTheSpotlight?: boolean;
    inTheSpotLight?: boolean;
    Type?: string;
    type?: string;
    Description?: string;
    description?: string;
    Content?: string;
    content?: string;
    Tags?: IMultimediaBackendTag[];
    tags?: IMultimediaBackendTag[];
}

export function MultimediaListWidget(props: IMultimediaListWidgetProps) {
    const frThemes: IDropDownValue[] = useMemo(
        () => [
            {
                id: '1',
                value: 'Trouver un job / Mijn 1ste job vinden',
                label: 'Trouver un job',
            },
            {
                id: '3',
                value: 'Retrouver un job / Opnieuw een job vinden',
                label: 'Retrouver un job',
            },
            {
                id: '5',
                value: 'Se réorienter / Me heroriënteren',
                label: 'Se réorienter',
            },
        ],
        []
    );

    const nlThemes: IDropDownValue[] = useMemo(
        () => [
            {
                id: '2',
                value: 'Trouver un job / Mijn 1ste job vinden',
                label: 'Mijn 1ste job vinden',
            },
            {
                id: '4',
                value: 'Retrouver un job / Opnieuw een job vinden',
                label: 'Opnieuw een job vinden',
            },
            {
                id: '6',
                value: 'Se réorienter / Me heroriënteren',
                label: 'Me heroriënteren',
            },
        ],
        []
    );
    const searchTextRef = useRef<string>('');
    const selectedThemesRef = useRef<string[]>([]);
    const pageSize = useRef<number>(9);
    const page = useRef<number>(1);
    const requestSeq = useRef<number>(0);

    const parseMaybeJson = useCallback(<T,>(value: unknown): T | null => {
        if (value == null) return null;
        if (typeof value !== 'string') return value as T;
        if (!value || value === 'null') return null;

        try {
            return JSON.parse(value) as T;
        } catch {
            return null;
        }
    }, []);

    const normalizeMedia = useCallback((m: IMultimediaBackend): IMultimedia => {
        const rawTags = Array.isArray(m.tags)
            ? m.tags
            : Array.isArray(m.Tags)
              ? m.Tags
              : [];

        return {
            id: Number(m.id ?? m.Id ?? 0),
            title: m.title ?? m.Title ?? '',
            creationDate: m.creationDate ?? m.CreationDate ?? '',
            youtubeLink: m.youtubeLink ?? m.YoutubeLink ?? '',
            description: m.description ?? m.Description ?? '',
            inTheSpotLight: m.inTheSpotLight ?? m.InTheSpotlight ?? false,
            type: m.type ?? m.Type ?? '',
            content: m.content ?? m.Content ?? '',
            thumbnailUrl: m.thumbnailUrl ?? m.ThumbnailUrl ?? '',
            readingTime: m.readingTime ?? m.ReadingTime ?? 0,
            tags: rawTags.map((tag) => ({
                id: tag.id ?? tag.Id ?? 0,
                title: tag.title ?? tag.Title ?? '',
            })),
        };
    }, []);

    const initialSelectedMultimedia = useMemo(() => {
        const parsed = parseMaybeJson<IMultimediaBackend>(
            props.selectedMultimedia
        );

        return parsed ? normalizeMedia(parsed) : null;
    }, [normalizeMedia, parseMaybeJson, props.selectedMultimedia]);

    const initialSelectedMedias = useMemo(() => {
        const parsed = parseMaybeJson<IMultimediaBackend[]>(
            props.selectedMedias
        );

        return parsed ? parsed.map(normalizeMedia) : null;
    }, [normalizeMedia, parseMaybeJson, props.selectedMedias]);

    const [state, setState] = useState<IMultimediaListWidgetState>({
        multimedias: null,
        multimediasTotalCount: 0,
        searchText: '',
        displayMore: false,
        isDisplayMoreBeenClicked: false,
        hasMore: true,
        themes: props.lang === 'fr' ? frThemes : nlThemes,
        selectedThemes: [],
        selectedMultimedia: initialSelectedMultimedia,
        isSearchTargetDisplayed: props.area === 'citizens',
        selectedMedias: initialSelectedMedias,
    });

    useEffect(() => {
        setState((prev) => ({
            ...prev,
            themes: props.lang === 'fr' ? frThemes : nlThemes,
            isSearchTargetDisplayed: props.area === 'citizens',
        }));
    }, [props.lang, props.area, frThemes, nlThemes]);

    useEffect(() => {
        searchTextRef.current = state.searchText;
        selectedThemesRef.current = state.selectedThemes;
    }, [state.searchText, state.selectedThemes]);

    const loadMedias = useCallback(
        async (p: number = page.current, ps: number = pageSize.current) => {
            const seq = ++requestSeq.current;

            const rawSearch = searchTextRef.current;
            const searchText = rawSearch.length > 2 ? rawSearch : null;

            const multimediasList = await MultimediaService.getAllMultimedias({
                page: p,
                pageSize: ps,
                target: props.area,
                searchText,
                themeIds: selectedThemesRef.current,
                tagIds: null,
            });

            if (seq !== requestSeq.current) return;

            setState((prev) => {
                const next =
                    p === 1
                        ? [...multimediasList.multimediaContents]
                        : [
                              ...(prev.multimedias ?? []),
                              ...multimediasList.multimediaContents,
                          ];

                const total = multimediasList.multimediaContentsCount ?? 0;

                return {
                    ...prev,
                    multimedias: next,
                    multimediasTotalCount: total,
                    hasMore: next.length < total,
                };
            });
        },
        [props.area]
    );

    useEffect(() => {
        void loadMedias(1, 5);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEvent = useCallback(() => {
        const url: string = window.location.search;
        if (url !== '') {
            const ref = url.match(/[0-9]+/);
            if (ref !== null && state.multimedias) {
                const multimedia = state.multimedias.find(
                    (m) => m.id.toString() === ref[0]
                );
                if (multimedia) {
                    setState((prev) => ({
                        ...prev,
                        selectedMultimedia: { ...(multimedia as any) },
                    }));
                }
            }
        } else {
            setState((prev) => ({ ...prev, selectedMultimedia: undefined }));
        }
    }, [state.multimedias]);

    useEffect(() => {
        window.addEventListener('popstate', handleEvent);
        return () => window.removeEventListener('popstate', handleEvent);
    }, [handleEvent]);

    const displayMoreClicked = useCallback(() => {
        page.current = 1;
        setState((prev) => ({
            ...prev,
            multimedias: null,
            hasMore: true,
            displayMore: true,
            isDisplayMoreBeenClicked: true,
        }));

        void loadMedias().then(() => window.scrollTo(0, 0));
    }, [loadMedias]);

    const mediaClicked = useCallback((multimedia: IMultimedia) => {
        const id = multimedia.id;

        window.history.pushState({ reference: id }, '', '?reference=' + id);

        const listLinkLang =
            document.querySelectorAll<HTMLAnchorElement>('a.language-link');
        listLinkLang.forEach((link) => {
            link.href =
                link.href.replace(/\?reference=[0-9]+$/, '') +
                `?reference=${id}`;
        });

        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

        setState((prev) => ({
            ...prev,
            selectedMultimedia: { ...multimedia },
        }));
    }, []);

    const unselectMedia = useCallback((): void => {
        window.history.pushState(
            null,
            '',
            `https://${window.location.host + window.location.pathname}`
        );

        const listLinkLang =
            document.querySelectorAll<HTMLAnchorElement>('a.language-link');
        listLinkLang.forEach((link) => {
            link.href = link.href.replace(/\?reference=[0-9]+#*$/, '');
        });

        setState((prev) => ({ ...prev, selectedMultimedia: undefined }));
    }, []);

    const searchTextChanged = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            const prevValue = searchTextRef.current;

            searchTextRef.current = value;

            const enteringSearch = value.length > 2;
            const leavingSearch = prevValue.length > 2 && value.length <= 2;

            setState((prev) => ({
                ...prev,
                searchText: value,
                multimedias: null,
                hasMore: true,
                displayMore: enteringSearch ? true : prev.displayMore,
                isDisplayMoreBeenClicked: enteringSearch
                    ? true
                    : prev.isDisplayMoreBeenClicked,
            }));

            if (enteringSearch || leavingSearch) {
                page.current = 1;
                void loadMedias(1, pageSize.current);
            }
        },
        [loadMedias]
    );

    const filterChanged = useCallback(
        (event: readonly IDropDownValue[]) => {
            const themes = event.map((e) => e.value);

            selectedThemesRef.current = themes;

            setState((prev) => ({
                ...prev,
                selectedThemes: themes,
                multimedias: null,
                hasMore: true,
            }));

            page.current = 1;
            void loadMedias(1, pageSize.current);
        },
        [loadMedias]
    );

    const fetchMoreData = useCallback(() => {
        const { multimedias, multimediasTotalCount } = state;

        if (multimedias && multimedias.length >= multimediasTotalCount) {
            setState((prev) => ({ ...prev, hasMore: false }));
            return;
        }

        page.current++;
        void loadMedias(page.current, pageSize.current);
    }, [loadMedias, state]);

    const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
    }, []);

    const loader = (
        <div className="lds-ripple" key={'loader'}>
            <div></div>
            <div></div>
        </div>
    );

    const {
        selectedMultimedia,
        multimedias,
        displayMore,
        hasMore,
        searchText,
        themes,
        selectedMedias,
    } = state;

    let content: React.ReactNode;
    let displaySearchBar = true;

    if (selectedMultimedia) {
        displaySearchBar = false;
        content = (
            <MultimediaDetail
                dictBackLabel={props.dictBackLabel}
                dictSimilarMedias={props.dictSimilarMedias}
                multimedia={selectedMultimedia}
                area={props.area}
                unselectMultimedia={() => unselectMedia()}
                selectMedia={(media) => mediaClicked(media)}
            />
        );
    } else if (!multimedias) {
        content = <>{loader}</>;
    } else if (multimedias.length === 0) {
        content = (
            <div className="container">
                <div className="row">
                    <div className="col-12 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
                        <div className="row">
                            <span>{props.dictEmptyList}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else if (displayMore) {
        content = (
            <div className="container">
                <div className="row">
                    <div
                        className="col-12 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2 position-relative"
                        style={{ zIndex: 1 }}
                    >
                        <div className="row">
                            <InfiniteScroll
                                loadMore={fetchMoreData}
                                hasMore={hasMore}
                                loader={loader}
                                className="w-100"
                            >
                                <div className="row">
                                    {multimedias.map((multimedia) => (
                                        <div
                                            tabIndex={5}
                                            key={multimedia.id}
                                            className="my-2 px-md-1 px-5 col-lg-4 col-md-6 col-12 animation-trigger"
                                        >
                                            <MultimediaPreview
                                                multimedia={multimedia}
                                                onClick={() =>
                                                    mediaClicked(multimedia)
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                            </InfiniteScroll>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else {
        content = (
            <div className="container">
                <div className="row">
                    <div
                        className="col-12 position-relative"
                        style={{ zIndex: 1 }}
                    >
                        <div className="row">
                            <div className="col-md-8 col-12">
                                <div className="row">
                                    {multimedias.map((multimedia, index) => (
                                        <div
                                            tabIndex={1}
                                            key={multimedia.id}
                                            className={
                                                'my-3 animation-trigger ' +
                                                (index === 0
                                                    ? 'col-12'
                                                    : 'col-md-6 col-12')
                                            }
                                        >
                                            <MultimediaPreview
                                                multimedia={multimedia}
                                                onClick={() =>
                                                    mediaClicked(multimedia)
                                                }
                                                big={index === 0}
                                            />
                                        </div>
                                    ))}

                                    {!displayMore && (
                                        <div className="col-12 my-3">
                                            <button
                                                tabIndex={2}
                                                className="btn-primary w-100"
                                                onClick={displayMoreClicked}
                                            >
                                                {props.dictReadMore}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-4 col-12">
                                {selectedMedias &&
                                    selectedMedias.length !== 0 && (
                                        <div className="row">
                                            <div className="col-12">
                                                <h1>{props.dictTheMostRead}</h1>
                                            </div>

                                            {selectedMedias.map(
                                                (media, index) => {
                                                    const date = new Date(
                                                        media.creationDate
                                                    );
                                                    return (
                                                        <React.Fragment
                                                            key={
                                                                media.id ??
                                                                `${media.title}-${index}`
                                                            }
                                                        >
                                                            <div className="col-1 d-flex align-items-center highlighted-badge">
                                                                <span>
                                                                    {index + 1}
                                                                </span>
                                                            </div>
                                                            <div
                                                                className={
                                                                    'col-11 highlighted-item border-bottom border-right' +
                                                                    (index === 0
                                                                        ? ' border-top'
                                                                        : '')
                                                                }
                                                            >
                                                                <a
                                                                    tabIndex={3}
                                                                    onClick={() =>
                                                                        mediaClicked(
                                                                            media
                                                                        )
                                                                    }
                                                                >
                                                                    {
                                                                        media.title
                                                                    }
                                                                </a>
                                                                <br />
                                                                <span>
                                                                    {date.toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                        </React.Fragment>
                                                    );
                                                }
                                            )}
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-100">
            {displaySearchBar && (
                <form
                    className="container"
                    onSubmit={handleSubmit}
                    data-skip-captcha="true"
                >
                    <div
                        className="row my-5 d-flex justify-content-center media-search position-relative"
                        style={{ zIndex: 10 }}
                    >
                        <div className="form-group-field">
                            <label
                                htmlFor="searchBar"
                                className="sr-only sr-only-focusable"
                            >
                                {props.dictSearchPlaceholder}
                            </label>
                            <div>
                                <input
                                    key="searchBar"
                                    type="text"
                                    value={searchText}
                                    placeholder={props.dictSearchPlaceholder}
                                    onChange={searchTextChanged}
                                    tabIndex={4}
                                />
                            </div>
                        </div>

                        {state.isSearchTargetDisplayed && (
                            <div className="form-group-field">
                                <div className="field-icon objectif media-search w-100">
                                    {themes && themes.length !== 0 && (
                                        <Select
                                            isMulti
                                            options={themes}
                                            onChange={filterChanged}
                                            isSearchable={false}
                                            hideSelectedOptions={false}
                                            className="domain-select-container"
                                            classNamePrefix="domain-select"
                                            closeMenuOnSelect={false}
                                            placeholder={props.dictSelectDomain}
                                            tabIndex={5}
                                        />
                                    )}
                                </div>

                                {state.selectedThemes &&
                                    state.selectedThemes.length > 0 && (
                                        <div className="selected-value selected-value__with-icon">
                                            {state.selectedThemes.length}{' '}
                                            {props.dictSelectedThemes}
                                        </div>
                                    )}
                            </div>
                        )}

                        <div className="form-group submit p-1 mb-0">
                            <button
                                type="submit"
                                className="theme-reactive h-100"
                            >
                                <i className="icon-search" tabIndex={5}></i>
                            </button>
                        </div>
                    </div>
                </form>
            )}

            {content}
        </div>
    );
}

WidgetHelper.register(MultimediaListWidget, 'multimedia-list-widget');
